import { isRunning } from '../components/BrandContext/brandIdentity';
import { contrastRatio, deriveDarkColors, failsAA, isHexColor, mixHex } from './brandColors';

// The sales agent widget's colors (chatBot.appearance.colors), resolved the way the API serves
// them, for the Customize tab's preview and contrast checks. Keep in step with the API's
// helpers/agent/widget-theme-colors.js.

const SOURCES = ['default', 'brand', 'custom'];
const MODES = ['light', 'dark', 'auto'];

// Widget roles, where each paints and which brand kit role feeds it on each side.
export const WIDGET_ROLES = [
  {
    key: 'primary',
    label: 'Main color',
    where: "Launcher, your shopper's messages, send button, links",
    light: 'primary',
    dark: 'link',
  },
  {
    key: 'background',
    label: 'Chat window',
    where: 'Panel, header, assistant messages, input bar',
    light: 'content',
    dark: 'content',
  },
  { key: 'surface', label: 'Conversation', where: 'Area behind the messages', light: 'page', dark: 'page' },
  { key: 'text', label: 'Text', where: 'Messages, headings, product names', light: 'text', dark: 'text' },
  { key: 'textMuted', label: 'Muted text', where: 'Hints, timestamps, prices', light: 'textMuted', dark: 'textMuted' },
];

// The widget's own palette (chat-bot-sdk tokens): what a role shows when nothing sets it.
export const WIDGET_DEFAULTS = {
  light: { primary: '#904dff', background: '#ffffff', surface: '#fafafa', text: '#333333', textMuted: '#999999' },
  dark: { primary: '#904dff', background: '#1a1a1a', surface: '#262626', text: '#e5e5e5', textMuted: '#737373' },
};

const pickHex = (values) => Object.fromEntries(Object.entries(values || {}).filter(([, value]) => isHexColor(value)));

// The stored settings with their defaults filled in; a row without them is today's widget.
const colorSettings = (colors) => ({
  source: SOURCES.includes(colors?.source) ? colors.source : 'default',
  mode: MODES.includes(colors?.mode) ? colors.mode : 'light',
  light: pickHex(colors?.light),
  dark: pickHex(colors?.dark),
});

// Only an approved brand with colors feeds the widget: an unreviewed website read never does.
const brandColorsReady = (brandContext) =>
  !!brandContext?.approvedAt && Object.keys(pickHex(brandContext.visual?.colors)).length > 0;

export const brandColorsState = (brandContext) => {
  if (brandColorsReady(brandContext)) return 'confirmed';
  if (!brandContext || brandContext.status === 'skipped') return 'missing';
  if (isRunning(brandContext.status)) return 'running';
  return brandContext.approvedAt ? 'missing' : 'proposed';
};

// Black or white, whichever reads better on the color.
const onColor = (hex) =>
  (contrastRatio(hex, '#ffffff') ?? 0) >= (contrastRatio(hex, '#111111') ?? 0) ? '#ffffff' : '#111111';

// The brand's values per side, each tagged with where it came from ("colors.content",
// "darkColors.content", or "derived.content" when the brand has no dark palette of its own).
const brandValues = (visual) => {
  const colors = pickHex(visual?.colors);
  const darkColors = pickHex(visual?.darkColors);
  const derived = pickHex(deriveDarkColors(colors));
  const light = {};
  const dark = {};
  WIDGET_ROLES.forEach((role) => {
    if (colors[role.light]) light[role.key] = { value: colors[role.light], from: `colors.${role.light}` };
    if (darkColors[role.dark]) dark[role.key] = { value: darkColors[role.dark], from: `darkColors.${role.dark}` };
    else if (derived[role.dark]) dark[role.key] = { value: derived[role.dark], from: `derived.${role.dark}` };
  });
  return { light, dark };
};

// Each side's palette, where each role came from ('default', 'legacy', 'override' or a brand
// key), and the automatic text color on the main color.
//   default: the widget's palette; a legacy customThemeColor still sets the main color.
//   brand:   the approved brand, or the default when the brand is not ready.
//   custom:  the approved brand (when ready) with the client's changes on top.
// The main color is one hex in the widget, so only a pinned Dark appearance uses the dark one.
export const resolveWidgetColors = ({ colors, customThemeColor, brandContext }) => {
  const settings = colorSettings(colors);
  const ready = brandColorsReady(brandContext);
  const useBrand = ready && settings.source !== 'default';
  const brand = useBrand ? brandValues(brandContext.visual) : { light: {}, dark: {} };
  const legacy = !useBrand && settings.source !== 'custom' && isHexColor(customThemeColor) ? customThemeColor : null;

  const side = (name) => {
    const palette = {};
    const from = {};
    WIDGET_ROLES.forEach(({ key }) => {
      // The main color is only set on the light side.
      const overrides = name === 'dark' && key === 'primary' ? settings.light : settings[name];
      const override = settings.source === 'custom' ? overrides[key] : null;
      if (override) {
        palette[key] = override;
        from[key] = 'override';
      } else if (brand[name][key]) {
        palette[key] = brand[name][key].value;
        from[key] = brand[name][key].from;
      } else if (key === 'primary' && legacy) {
        palette[key] = legacy;
        from[key] = 'legacy';
      } else {
        palette[key] = WIDGET_DEFAULTS[name][key];
        from[key] = 'default';
      }
    });
    return { palette, from };
  };

  const light = side('light');
  const dark = side('dark');
  if (settings.mode !== 'dark') {
    dark.palette.primary = light.palette.primary;
    dark.from.primary = light.from.primary;
  }
  [light, dark].forEach((s) => {
    s.palette.onPrimary = onColor(s.palette.primary);
  });
  return { settings, ready, light, dark };
};

// The pairs a shopper has to read, checked at AA.
const CONTRAST_PAIRS = [
  { role: 'text', on: 'background', label: 'Text on Chat window' },
  { role: 'text', on: 'surface', label: 'Text on Conversation' },
  { role: 'textMuted', on: 'background', label: 'Muted text on Chat window' },
  { role: 'primary', on: 'onPrimary', label: 'Text on Main color' },
];

// Failing pairs of one side. The widget's own defaults are not the client's to fix, so a pair
// made only of them is not flagged.
export const contrastIssues = ({ palette, from }) =>
  CONTRAST_PAIRS.filter(({ role, on }) => {
    const set = from[role] !== 'default' || (on !== 'onPrimary' && from[on] !== 'default');
    return set && failsAA(palette[role], palette[on]);
  }).map((pair) => ({ ...pair, ratio: contrastRatio(palette[pair.role], palette[pair.on]) }));

const passes = (role, value, palette) =>
  CONTRAST_PAIRS.filter((pair) => pair.role === role).every(({ on }) =>
    on === 'onPrimary' ? !failsAA(value, onColor(value)) : !failsAA(value, palette[on])
  );

// The closest color to the role's value, nudged toward black or white, that reads at AA in
// every pair of that role. Null when nothing does.
export const readableFix = (role, palette) => {
  for (let step = 1; step <= 20; step += 1) {
    const weight = 1 - step * 0.05;
    const found = ['#000000', '#ffffff']
      .map((target) => mixHex(palette[role], target, weight).toLowerCase())
      .find((candidate) => passes(role, candidate, palette));
    if (found) return found;
  }
  return null;
};
